import { useState } from 'react';
import axios from 'axios';
import { Bell, CheckCircle2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact price-drop SMS opt-in widget.
 * Props: vehicleId
 */
export default function PriceDropAlert({ vehicleId }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '' });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.customer_phone) {
      toast.error(t('carvana.priceAlert.errFill', 'Merci de remplir tous les champs'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/price-alerts/subscribe`, {
        vehicle_id: vehicleId,
        lang: i18n.language?.startsWith('en') ? 'en' : 'fr',
        ...form,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3" data-testid="price-alert-done">
        <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
        <p className="text-sm text-slate-700">
          <span className="font-bold">{t('carvana.priceAlert.doneTitle', 'Alerte activée!')}</span>{' '}
          {t('carvana.priceAlert.doneDesc', "Nous vous enverrons un texto dès que le prix baisse.")}
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-2xl p-4 flex items-center gap-3 text-left transition-colors"
        data-testid="price-alert-open-btn"
      >
        <Bell className="h-6 w-6 text-amber-600 flex-shrink-0" />
        <div>
          <p className="font-bold text-slate-900 text-sm">{t('carvana.priceAlert.title', "M'avertir d'une baisse de prix")}</p>
          <p className="text-xs text-slate-600">{t('carvana.priceAlert.subtitle', 'Recevez un texto instantané quand ce véhicule baisse.')}</p>
        </div>
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-amber-200 rounded-2xl p-5 relative" data-testid="price-alert-form">
      <button type="button" onClick={() => setOpen(false)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
      <div className="flex items-center gap-2 mb-3">
        <Bell className="h-5 w-5 text-amber-600" />
        <h3 className="font-bold text-slate-900">{t('carvana.priceAlert.formTitle', 'Alerte baisse de prix par SMS')}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">{t('carvana.priceAlert.formSubtitle', 'Vous recevrez UN seul texto quand le prix baisse. Aucun spam.')}</p>
      <div className="space-y-2">
        <input required placeholder={t('carvana.priceAlert.name', 'Nom')} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="price-alert-name" />
        <input required type="email" placeholder={t('carvana.priceAlert.email', 'Courriel')} value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="price-alert-email" />
        <input required type="tel" placeholder={t('carvana.priceAlert.phone', 'Téléphone (+15145551234)')} value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="price-alert-phone" />
      </div>
      <button type="submit" disabled={loading} className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-white font-bold py-2.5 rounded-lg text-sm disabled:opacity-50" data-testid="price-alert-submit-btn">
        {loading ? t('carvana.priceAlert.loading', 'Envoi...') : t('carvana.priceAlert.submit', "M'inscrire")}
      </button>
    </form>
  );
}
